import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Field, FieldGroup } from '@/components/Field';
import { LocationPicker } from '@/components/LocationPicker';
import { SelectionCards } from '@/components/SelectionCards';
import { Stepper } from '@/components/Stepper';
import { statusLabel } from '@/utils/surveyStatus';
import {
  LOCATION_TYPE_OPTIONS,
  ROAD_CONFIGURATION_OPTIONS,
  ROAD_SURFACE_OPTIONS,
  WEATHER_OPTIONS,
} from '@/config/surveyOptions';
import {
  SETTING_LAST_SURVEYOR,
  createSurvey,
  deleteSurvey,
  findActiveSurvey,
  getSetting,
  setSetting,
} from '@/db/localDatabase';
import { useConfirm } from '@/hooks/useConfirm';
import type { Survey } from '@/types/survey';
import { generateSurveyId } from '@/utils/ids';
import { toLocalDateString, toLocalTimeString } from '@/utils/format';
import {
  firstInvalidField,
  isValid,
  validateSurveyForm,
  type SurveyFormValues,
  type ValidationErrors,
} from '@/utils/validation';

function emptyValues(): SurveyFormValues {
  const now = new Date();
  return {
    surveyorName: '',
    // Device date and time, pre-filled but fully overridable.
    surveyDate: toLocalDateString(now),
    plannedStartTime: toLocalTimeString(now),
    latitude: '',
    longitude: '',
    gpsAccuracy: '',
    district: '',
    manualLocation: '',
    roadNumber: '',
    mccNumber: '',
    weather: '',
    locationType: '',
    roadSurface: '',
    roadConfiguration: '',
    direction: '',
    laneCount: 2,
  };
}

export function SurveyFormPage() {
  const navigate = useNavigate();
  const { pending, confirm, answer } = useConfirm();

  const [values, setValues] = useState<SurveyFormValues>(emptyValues);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [gpsCapturedAt, setGpsCapturedAt] = useState<number | undefined>();
  const [existing, setExisting] = useState<Survey | null>(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Guards against a double-tapped Continue creating two surveys.
  const creatingRef = useRef(false);

  // On startup, never silently discard an unfinished survey.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [active, lastName] = await Promise.all([
        findActiveSurvey(),
        getSetting(SETTING_LAST_SURVEYOR),
      ]);
      if (cancelled) return;
      if (active) setExisting(active);
      if (lastName)
        setValues((current) => ({ ...current, surveyorName: current.surveyorName || lastName }));
      setChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = useCallback((changes: Partial<SurveyFormValues> & { gpsCapturedAt?: number }) => {
    const { gpsCapturedAt: capturedAt, ...rest } = changes;
    if (capturedAt) setGpsCapturedAt(capturedAt);
    setValues((current) => ({ ...current, ...rest }));
    // Clear the error for any field the user is actively fixing.
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(rest) as (keyof SurveyFormValues)[]) delete next[key];
      return next;
    });
  }, []);

  const scrollToField = (field: string) => {
    const element = document.querySelector<HTMLElement>(`[data-field="${field}"]`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element?.querySelector<HTMLElement>('input, select, textarea, button')?.focus({
      preventScroll: true,
    });
  };

  const handleContinue = async () => {
    if (creatingRef.current) return;

    const validation = validateSurveyForm(values);
    setErrors(validation);
    if (!isValid(validation)) {
      const field = firstInvalidField(validation);
      if (field) scrollToField(field);
      return;
    }

    creatingRef.current = true;
    setSubmitting(true);
    setFormError(null);

    try {
      const latitude = values.latitude.trim() ? Number(values.latitude) : undefined;
      const longitude = values.longitude.trim() ? Number(values.longitude) : undefined;
      const accuracy = values.gpsAccuracy.trim() ? Number(values.gpsAccuracy) : undefined;

      const survey = await createSurvey({
        id: generateSurveyId(new Date(`${values.surveyDate}T00:00:00`)),
        surveyorName: values.surveyorName.trim(),
        surveyDate: values.surveyDate,
        plannedStartTime: values.plannedStartTime,
        latitude,
        longitude,
        gpsAccuracy: accuracy,
        gpsCapturedAt,
        district: values.district.trim(),
        manualLocation: values.manualLocation.trim(),
        roadNumber: values.roadNumber.trim(),
        mccNumber: values.mccNumber.trim(),
        weather: values.weather,
        locationType: values.locationType,
        roadSurface: values.roadSurface,
        roadConfiguration: values.roadConfiguration,
        direction: values.direction.trim(),
        laneCount: values.laneCount,
        status: 'ready',
        localFileGenerated: false,
        databaseSynced: false,
        telegramSent: false,
      });

      await setSetting(SETTING_LAST_SURVEYOR, survey.surveyorName);
      navigate(`/count/${survey.id}`, { replace: true });
    } catch (error) {
      console.error('Could not create the survey', error);
      setFormError(
        'The survey could not be saved on this phone. Close other tabs of this app and try again — nothing has been lost.',
      );
      creatingRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDiscardExisting = async () => {
    if (!existing) return;
    const ok = await confirm({
      title: 'Discard the unfinished survey?',
      body:
        `Survey ${existing.id} (${existing.roadNumber}) has not been submitted.\n\n` +
        'Its counts will be permanently deleted from this phone.',
      confirmLabel: 'Discard it',
      cancelLabel: 'Keep it',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteSurvey(existing.id, { force: true });
      setExisting(null);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not discard the survey.');
    }
  };

  if (!checked) {
    return (
      <>
        <AppHeader title="Traffic Survey" />
        <div className="page-body">
          <p className="text-slate-500">Loading saved data…</p>
        </div>
      </>
    );
  }

  return (
    <>
      <AppHeader title="Survey Information" subtitle="Step 1 of 3" />

      <div className="page-body space-y-5">
        {existing ? (
          <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4" role="alert">
            <h2 className="font-bold text-amber-900">
              {existing.status === 'completed'
                ? 'A finished survey has not been submitted yet.'
                : 'A survey is already in progress.'}
            </h2>
            <p className="mt-1 text-sm text-amber-900">
              {existing.id} · {existing.roadNumber || 'No road number'} ·{' '}
              {statusLabel(existing.status)}
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={() =>
                  navigate(
                    existing.status === 'completed'
                      ? `/summary/${existing.id}`
                      : `/count/${existing.id}`,
                  )
                }
              >
                {existing.status === 'completed' ? 'Finish Submitting' : 'Resume Existing Survey'}
              </button>
              {/* A completed survey holds real counts, so it is not offered for discard here. */}
              {existing.status === 'completed' ? null : (
                <button
                  type="button"
                  className="btn-secondary flex-1"
                  onClick={handleDiscardExisting}
                >
                  Discard Draft
                </button>
              )}
            </div>
          </div>
        ) : null}

        {formError ? (
          <div
            className="rounded-xl border-2 border-red-300 bg-red-50 p-4 text-red-900"
            role="alert"
          >
            {formError}
          </div>
        ) : null}

        {/*
          Everything typed on the keyboard is grouped first, then the large
          single-selection cards. Filling the text fields in one uninterrupted run
          is much faster in the field than alternating between typing and tapping.
        */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">
            Survey Details
          </h2>

          <Field id="surveyorName" label="Name" required error={errors.surveyorName}>
            <input
              id="surveyorName"
              type="text"
              autoComplete="name"
              className={`text-input ${errors.surveyorName ? 'text-input-invalid' : ''}`}
              value={values.surveyorName}
              maxLength={120}
              aria-invalid={Boolean(errors.surveyorName)}
              onChange={(event) => update({ surveyorName: event.target.value })}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field id="surveyDate" label="Date" required error={errors.surveyDate}>
              <input
                id="surveyDate"
                type="date"
                className={`text-input ${errors.surveyDate ? 'text-input-invalid' : ''}`}
                value={values.surveyDate}
                aria-invalid={Boolean(errors.surveyDate)}
                onChange={(event) => update({ surveyDate: event.target.value })}
              />
            </Field>
            <Field
              id="plannedStartTime"
              label="Start Time"
              required
              error={errors.plannedStartTime}
            >
              <input
                id="plannedStartTime"
                type="time"
                className={`text-input ${errors.plannedStartTime ? 'text-input-invalid' : ''}`}
                value={values.plannedStartTime}
                aria-invalid={Boolean(errors.plannedStartTime)}
                onChange={(event) => update({ plannedStartTime: event.target.value })}
              />
            </Field>
          </div>
          <p className="field-hint">
            Taken from this device. The real recording start time is saved separately when you press
            Start Count.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Field id="roadNumber" label="Road Number" required error={errors.roadNumber}>
              <input
                id="roadNumber"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                placeholder="A01"
                className={`text-input ${errors.roadNumber ? 'text-input-invalid' : ''}`}
                value={values.roadNumber}
                maxLength={60}
                aria-invalid={Boolean(errors.roadNumber)}
                onChange={(event) => update({ roadNumber: event.target.value })}
              />
            </Field>
            <Field id="mccNumber" label="MCC No" required error={errors.mccNumber}>
              <input
                id="mccNumber"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                placeholder="MCC-14"
                className={`text-input ${errors.mccNumber ? 'text-input-invalid' : ''}`}
                value={values.mccNumber}
                maxLength={60}
                aria-invalid={Boolean(errors.mccNumber)}
                onChange={(event) => update({ mccNumber: event.target.value })}
              />
            </Field>
          </div>

          <Field
            id="direction"
            label="Direction"
            required
            error={errors.direction}
            hint="Example: Colombo to Kandy"
          >
            <input
              id="direction"
              type="text"
              autoComplete="off"
              placeholder="Colombo to Kandy"
              className={`text-input ${errors.direction ? 'text-input-invalid' : ''}`}
              value={values.direction}
              maxLength={200}
              aria-invalid={Boolean(errors.direction)}
              onChange={(event) => update({ direction: event.target.value })}
            />
          </Field>

          <Field id="laneCount" label="No. of Lanes" required error={errors.laneCount}>
            <Stepper
              id="laneCount"
              value={values.laneCount}
              min={1}
              max={20}
              ariaLabel="number of lanes"
              invalid={Boolean(errors.laneCount)}
              onChange={(laneCount) => update({ laneCount })}
            />
          </Field>
        </section>

        <section className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Location</h2>
          <LocationPicker values={values} errors={errors} onChange={update} />
        </section>

        <section className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Conditions</h2>

          <FieldGroup
            id="weather"
            label="Weather Condition at Start"
            required
            error={errors.weather}
          >
            <SelectionCards
              name="weather"
              options={WEATHER_OPTIONS}
              value={values.weather}
              onChange={(weather) => update({ weather })}
            />
          </FieldGroup>

          <FieldGroup id="locationType" label="Location Type" required error={errors.locationType}>
            <SelectionCards
              name="locationType"
              options={LOCATION_TYPE_OPTIONS}
              value={values.locationType}
              onChange={(locationType) => update({ locationType })}
              columns={3}
            />
          </FieldGroup>

          <FieldGroup id="roadSurface" label="Road Surface" required error={errors.roadSurface}>
            <SelectionCards
              name="roadSurface"
              options={ROAD_SURFACE_OPTIONS}
              value={values.roadSurface}
              onChange={(roadSurface) => update({ roadSurface })}
              columns={3}
            />
          </FieldGroup>

          <FieldGroup
            id="roadConfiguration"
            label="Road Configuration"
            required
            error={errors.roadConfiguration}
          >
            <SelectionCards
              name="roadConfiguration"
              options={ROAD_CONFIGURATION_OPTIONS}
              value={values.roadConfiguration}
              onChange={(roadConfiguration) => update({ roadConfiguration })}
              columns={3}
            />
          </FieldGroup>
        </section>
      </div>

      <div className="sticky-footer">
        <button
          type="button"
          className="btn-primary w-full text-lg"
          onClick={handleContinue}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Continue'}
        </button>
        <p className="mt-2 text-center text-xs text-slate-500">
          Saved on this phone first. No internet needed.
        </p>
      </div>

      <ConfirmDialog request={pending} onAnswer={answer} />
    </>
  );
}
